import { COUNTRIES, UKRAINE_REGIONS, toCountryCode, toRegionValue } from '@/lib/constants/locations'
import type { SpecialtyOption, HealthcareRole } from '@/lib/specialties/types'
import { specialtyRequiredFor, specialtyHiddenFor } from '@/lib/specialties/types'
import { type FieldStates, type ConfigurableField, TODAY_RULE, stateOf } from '@/lib/registration/fieldStates'
import { toE164 } from '@/lib/utils/phone'

/**
 * The profile columns both profile-completion routes (course enrollment and
 * embed) need. Select these by name rather than `select('*')`: the routes
 * read a fixed shape (see ProfileForCompletion) and user_profiles carries
 * medical PII that neither route has any business shipping to the client.
 */
export const PROFILE_COMPLETION_COLUMNS =
  'first_name, last_name, patronymic, phone, date_of_birth, country, region, city, address, postal_code, ' +
  'specialty, specialty_id, secondary_specialty_ids, specialty_other, healthcare_role, is_not_working, ' +
  'education, education_other, workplace, position'

export interface ProfileForCompletion {
  first_name: string | null
  last_name: string | null
  patronymic: string | null
  phone: string | null
  date_of_birth: string | null
  country: string | null
  region: string | null
  city: string | null
  address: string | null
  postal_code: string | null
  specialty: string | null
  specialty_id: string | null
  secondary_specialty_ids: string[] | null
  specialty_other: string | null
  healthcare_role: HealthcareRole | null
  is_not_working: boolean | null
  education: string | null
  education_other: string | null
  workplace: string | null
  position: string | null
}

export interface FieldOption {
  value: string
  label_ua: string
  label_en?: string
}

export interface AdditionalField {
  id: string
  field_name: string
  field_type: 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'date' | 'phone' | 'email' | 'number'
  label_ua: string
  label_en?: string
  placeholder_ua?: string
  placeholder_en?: string
  is_required: boolean
  validation_rules?: Record<string, unknown>
  field_options?: {
    options: FieldOption[]
  }
  sort_order?: number
}

/** The full profile-field list, before a registration's field states are applied. */
function allProfileFields(profile: ProfileForCompletion, specialties: SpecialtyOption[]): AdditionalField[] {
  const role = profile.healthcare_role

  const fields: AdditionalField[] = [
    {
      id: 'basic_last_name',
      field_name: 'last_name',
      field_type: 'text',
      label_ua: 'Прізвище',
      label_en: 'Last Name',
      placeholder_ua: 'Бойко',
      placeholder_en: 'Boyko',
      is_required: true,
      sort_order: 1
    },
    {
      id: 'basic_first_name',
      field_name: 'first_name',
      field_type: 'text',
      label_ua: "Ім'я",
      label_en: 'First Name',
      placeholder_ua: 'Максим',
      placeholder_en: 'Maksym',
      is_required: true,
      sort_order: 2
    },
    {
      id: 'basic_patronymic',
      field_name: 'patronymic',
      field_type: 'text',
      label_ua: 'По батькові',
      label_en: 'Middle Name',
      placeholder_ua: 'Іванович',
      placeholder_en: 'Ivanovych',
      is_required: true,
      sort_order: 3
    },
    {
      id: 'basic_phone',
      field_name: 'phone',
      field_type: 'phone',
      label_ua: 'Телефон',
      label_en: 'Phone',
      placeholder_ua: '+380990282861',
      placeholder_en: '+380990282861',
      is_required: true,
      sort_order: 4
    },
    {
      id: 'basic_date_of_birth',
      field_name: 'date_of_birth',
      field_type: 'date',
      label_ua: 'Дата народження',
      label_en: 'Date of Birth',
      is_required: true,
      sort_order: 5
    },
    {
      id: 'basic_country',
      field_name: 'country',
      field_type: 'select',
      label_ua: 'Країна',
      label_en: 'Country',
      is_required: true,
      sort_order: 6,
      field_options: {
        options: COUNTRIES.map(c => ({
          value: c.value,
          label_ua: c.label_ua,
          label_en: c.label_en
        }))
      }
    },
    {
      id: 'basic_region',
      field_name: 'region',
      field_type: 'select',
      label_ua: 'Область',
      label_en: 'Region',
      is_required: true,
      sort_order: 7,
      field_options: {
        options: UKRAINE_REGIONS.map(r => ({
          value: r.value,
          label_ua: r.label,
          label_en: r.label
        }))
      }
    },
    {
      id: 'basic_city',
      field_name: 'city',
      field_type: 'text',
      label_ua: 'Місто',
      label_en: 'City',
      placeholder_ua: 'Київ',
      placeholder_en: 'Kyiv',
      is_required: true,
      sort_order: 8
    },
    {
      id: 'basic_address',
      field_name: 'address',
      field_type: 'text',
      label_ua: 'Адреса',
      label_en: 'Address',
      placeholder_ua: 'вул. Хрещатик, 1',
      placeholder_en: 'Khreshchatyk St., 1',
      is_required: false,
      sort_order: 9
    },
    {
      id: 'basic_postal_code',
      field_name: 'postal_code',
      field_type: 'text',
      label_ua: 'Поштовий індекс',
      label_en: 'Postal Code',
      placeholder_ua: '01001',
      placeholder_en: '01001',
      is_required: false,
      sort_order: 10
    },
    {
      id: 'basic_healthcare_role',
      field_name: 'healthcare_role',
      field_type: 'select',
      label_ua: "Ваша роль в охороні здоров'я",
      label_en: 'Your role in healthcare',
      is_required: true,
      sort_order: 10.5,
      field_options: {
        options: [
          { value: 'practitioner', label_ua: 'Медичний працівник (лікар, медсестра, фармацевт, реабілітолог, психолог та ін.)', label_en: 'Healthcare practitioner (physician, nurse, pharmacist, rehabilitation specialist, psychologist, etc.)' },
          { value: 'student', label_ua: 'Студент, інтерн, аспірант', label_en: 'Student, intern or resident' },
          { value: 'adjacent', label_ua: 'Суміжна сфера: фарма, медтех, освіта, адміністрування', label_en: 'Adjacent field: pharma, medtech, education, administration' },
          { value: 'none', label_ua: "Не працюю в охороні здоров'я", label_en: "I don't work in healthcare" },
        ]
      }
    },
  ]

  if (!specialtyHiddenFor(role)) {
    fields.push({
      id: 'basic_specialty_id',
      field_name: 'specialty_id',
      field_type: 'select',
      label_ua: 'Спеціальність',
      label_en: 'Specialty',
      // An empty catalog means the fetch failed (both routes degrade
      // gracefully rather than 500). A required select with no options can
      // never be satisfied, so the form would be unfinishable - ask for it
      // only when there is something to pick.
      is_required: specialties.length > 0 && specialtyRequiredFor(role),
      sort_order: 11,
      field_options: {
        options: specialties.map(s => ({
          value: s.id,
          label_ua: s.nameUk,
          label_en: s.nameEn
        }))
      }
    })
  }

  fields.push({
    id: 'basic_education',
    field_name: 'education',
    field_type: 'select',
    label_ua: 'Освіта',
    label_en: 'Education',
    is_required: true,
    sort_order: 12,
    field_options: {
      options: [
        { value: 'Вища медична', label_ua: 'Вища медична', label_en: 'Higher medical' },
        { value: 'Вища немедична', label_ua: 'Вища немедична', label_en: 'Higher non-medical' },
        { value: 'Середня медична', label_ua: 'Середня медична', label_en: 'Middle medical' },
        { value: 'Професійно-технічна', label_ua: 'Професійно-технічна', label_en: 'Professional-technical' },
        { value: 'Інша', label_ua: 'Інша', label_en: 'Other' },
      ]
    }
  })

  fields.push({
    id: 'basic_is_not_working',
    field_name: 'is_not_working',
    field_type: 'checkbox',
    label_ua: 'Зараз не працюю',
    label_en: 'Not currently working',
    is_required: false,
    sort_order: 12.5
  })

  fields.push({
    id: 'basic_workplace',
    field_name: 'workplace',
    field_type: 'text',
    label_ua: 'Місце роботи',
    label_en: 'Workplace',
    placeholder_ua: 'Київська міська лікарня №1',
    placeholder_en: 'Kyiv City Hospital #1',
    is_required: !profile.is_not_working,
    sort_order: 13
  })

  fields.push({
    id: 'basic_position',
    field_name: 'position',
    field_type: 'text',
    label_ua: 'Посада',
    label_en: 'Position',
    placeholder_ua: 'Лікар загальної практики',
    placeholder_en: 'General Practitioner',
    is_required: !profile.is_not_working,
    sort_order: 14
  })

  return fields
}

const STATE_KEY: Record<string, ConfigurableField | undefined> = {
  patronymic: 'patronymic', phone: 'phone', date_of_birth: 'date_of_birth', country: 'country',
  region: 'region', city: 'city', address: 'address', postal_code: 'postal_code',
  healthcare_role: 'healthcare_role', specialty_id: 'specialty', education: 'education',
  workplace: 'workplace', position: 'position',
}

/**
 * The profile fields a registration asks for, given the ticket's field
 * states (D1 of docs/superpowers/specs/2026-10-19-registration-forms-design.md),
 * and the source of truth for what counts as a complete profile. Without
 * states this is today's rule, so every caller that passes none behaves as
 * before.
 *
 * Names are always required. `specialty_id` follows the role: required for
 * a practitioner when its state is `required` and the catalog is non-empty,
 * optional for a student, not asked for `adjacent` / `none` or when the role
 * is not asked. `is_not_working` is asked whenever workplace or position is,
 * and waives both.
 */
export function buildProfileFields(
  profile: ProfileForCompletion,
  specialties: SpecialtyOption[],
  states: FieldStates = TODAY_RULE
): AdditionalField[] {
  const role = profile.healthcare_role
  const roleAsked = stateOf(states, 'healthcare_role') !== 'hidden'
  const workAsked = stateOf(states, 'workplace') !== 'hidden' || stateOf(states, 'position') !== 'hidden'

  return allProfileFields(profile, specialties).flatMap(field => {
    const name = field.field_name
    if (name === 'first_name' || name === 'last_name') return [{ ...field, is_required: true }]
    if (name === 'is_not_working') return workAsked ? [{ ...field, is_required: false }] : []
    const key = STATE_KEY[name]
    if (!key) return [field]
    const state = stateOf(states, key)
    if (state === 'hidden') return []
    if (name === 'specialty_id') {
      if (!roleAsked || role === 'adjacent' || role === 'none') return []
      if (role === 'student') return [{ ...field, is_required: false }]
      return [{ ...field, is_required: state === 'required' && specialties.length > 0 && role === 'practitioner' }]
    }
    if (name === 'workplace' || name === 'position') {
      return [{ ...field, is_required: state === 'required' && !profile.is_not_working }]
    }
    return [{ ...field, is_required: state === 'required' }]
  })
}

/** Every column value as a string the form fields can bind to. */
/**
 * What handle_new_user used to write when an account was created without a
 * name (email code sign-up, e.g. in the embed widget). Names are now left
 * null instead; this stays as a safety net for any row still holding them.
 * Not a name: these count as missing and show as empty inputs, or
 * registration goes through with «Користувач -» on the ticket and the
 * certificate.
 */
const NAME_PLACEHOLDERS: Record<string, string> = { first_name: 'Користувач', last_name: '-' }

export function isNamePlaceholder(field: string, value: unknown): boolean {
  return typeof value === 'string' && field in NAME_PLACEHOLDERS && value.trim() === NAME_PLACEHOLDERS[field]
}

export function basicDataForForm(profile: ProfileForCompletion): Record<string, string> {
  const str = (v: string | null | undefined): string => v ?? ''
  const name = (field: 'first_name' | 'last_name') =>
    isNamePlaceholder(field, profile[field]) ? '' : str(profile[field])

  return {
    first_name: name('first_name'),
    last_name: name('last_name'),
    patronymic: str(profile.patronymic),
    // Legacy rows still hold '380671234567' or '0671234567'; PhoneInput only
    // understands E.164, so normalize on read. An unparsable value is passed
    // through untouched rather than blanked - the user can see and fix it.
    phone: toE164(profile.phone) ?? str(profile.phone),
    date_of_birth: str(profile.date_of_birth),
    // Legacy rows hold "Україна"/"Ukraine" and "Львівська область": read them
    // as the pickers' values, or the country shows as unpicked and the oblast
    // picker falls back to free text. Saving the form stores the code.
    country: toCountryCode(profile.country),
    region: toRegionValue(profile.region),
    city: str(profile.city),
    address: str(profile.address),
    postal_code: str(profile.postal_code),
    specialty: str(profile.specialty),
    specialty_id: str(profile.specialty_id),
    secondary_specialty_ids: JSON.stringify(profile.secondary_specialty_ids ?? []),
    specialty_other: str(profile.specialty_other),
    healthcare_role: str(profile.healthcare_role),
    is_not_working: profile.is_not_working ? 'true' : '',
    education: str(profile.education),
    education_other: str(profile.education_other),
    workplace: str(profile.workplace),
    position: str(profile.position),
  }
}

export function missingProfileFields(profile: ProfileForCompletion, fields: AdditionalField[]): string[] {
  return fields
    .filter(field => {
      if (!field.is_required) return false
      // Array-valued columns don't fit the string/null emptiness check below
      // and aren't required as their own field today.
      if (field.field_name === 'secondary_specialty_ids') return false
      const value = (profile as unknown as Record<string, unknown>)[field.field_name]
      if (value === null || value === undefined) return true
      if (isNamePlaceholder(field.field_name, value)) return true
      if (typeof value === 'string') return value.trim() === ''
      return false
    })
    .map(field => field.field_name)
}
